import React, { useMemo } from 'react';
import { FaChartPie } from 'react-icons/fa';
import { IngredientPurchaseRecord } from '../../../../types';
import './IngredientBreakdown.scss';

interface IngredientBreakdownProps {
  purchases: IngredientPurchaseRecord[];
}

interface IngredientStat {
  name: string;
  totalCost: number;
  quantity: number;
  purchaseCount: number;
  percentage: number;
}

const formatCurrency = (value: number) => 
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value);

const IngredientBreakdown: React.FC<IngredientBreakdownProps> = ({ purchases }) => {
  
  const ingredientStats = useMemo(() => {
    if (!purchases || purchases.length === 0) return [];

    // Group by ingredient
    const ingredientMap = new Map<string, { totalCost: number; quantity: number; count: number }>();
    let totalCost = 0;

    purchases.forEach(p => {
      const ingredient = p.ingredient_name || 'Sin nombre';
      const existing = ingredientMap.get(ingredient) || { totalCost: 0, quantity: 0, count: 0 };
      ingredientMap.set(ingredient, {
        totalCost: existing.totalCost + p.total_neto,
        quantity: existing.quantity + p.quantity,
        count: existing.count + 1,
      });
      totalCost += p.total_neto;
    });

    // Convert to array and calculate percentages
    const stats: IngredientStat[] = Array.from(ingredientMap.entries()).map(([name, data]) => ({
      name,
      totalCost: data.totalCost,
      quantity: data.quantity,
      purchaseCount: data.count,
      percentage: (data.totalCost / totalCost) * 100,
    }));

    // Sort by total cost descending
    stats.sort((a, b) => b.totalCost - a.totalCost);

    return stats.slice(0, 10); // Top 10
  }, [purchases]);

  if (ingredientStats.length === 0) {
    return null;
  }

  return (
    <div className="ingredient-breakdown-wrapper">
      <div className="breakdown-header">
        <h3><FaChartPie className="chart-icon" /> Top Gastos</h3>
        <p>Clasificación por costo total</p>
      </div>
      
      <div className="breakdown-list">
        {ingredientStats.map((ingredient, index) => (
          <div key={index} className={`breakdown-item rank-${index + 1}`}>
            <div className="rank-number">{index + 1}</div>
            
            <div className="ingredient-info">
              <div className="ingredient-name">{ingredient.name}</div>
              <div className="ingredient-meta">
                {ingredient.purchaseCount} compras · {ingredient.quantity.toFixed(2)} unidades
              </div>
            </div>

            <div className="ingredient-cost">
              <div className="cost-amount">{formatCurrency(ingredient.totalCost)}</div>
              <div className="cost-bar">
                <div 
                  className="cost-fill" 
                  style={{ width: `${ingredient.percentage}%` }}
                />
              </div>
              <div className="cost-percentage">{ingredient.percentage.toFixed(1)}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IngredientBreakdown;
